import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useContentSetting } from "./ContentSetting";
import {
  HIGHLIGHT_COLORS,
  HIGHLIGHT_TOOLS,
  canUseScreenHighlight,
  createStrokeId,
  isShapeTool,
  isTinyShape,
  pointerToNormalized,
  strokeWidthFor,
} from "../utils/highlightCoords";

const ScreenHighlightContext = createContext(null);

export const useScreenHighlight = () => useContext(ScreenHighlightContext);

export function ScreenHighlightProvider({ children }) {
  const { userInfo } = useContentSetting();
  const canHighlight = canUseScreenHighlight(userInfo?.user_type);

  const [toolbarOpen, setToolbarOpen] = useState(false);
  const [tool, setTool] = useState(HIGHLIGHT_TOOLS.NONE);
  const [color, setColor] = useState(HIGHLIGHT_COLORS[0]);
  const [thickness, setThickness] = useState("m");
  const [strokes, setStrokes] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [liveStroke, setLiveStroke] = useState(null);
  const [laser, setLaser] = useState(null);

  const overlayRef = useRef(null);
  const drawingRef = useRef(false);
  const liveStrokeRef = useRef(null);

  const isDrawingTool = toolbarOpen && tool !== HIGHLIGHT_TOOLS.NONE;

  const toggleToolbar = useCallback(() => {
    setToolbarOpen((open) => !open);
  }, []);

  const closeToolbar = useCallback(() => {
    setToolbarOpen(false);
  }, []);

  useEffect(() => {
    if (toolbarOpen) return;
    drawingRef.current = false;
    liveStrokeRef.current = null;
    setLiveStroke(null);
    setLaser(null);
  }, [toolbarOpen]);

  const beginStroke = useCallback(
    (clientX, clientY, shiftKey = false) => {
      if (!isDrawingTool || tool === HIGHLIGHT_TOOLS.LASER) return;
      const point = pointerToNormalized(clientX, clientY, overlayRef.current);
      drawingRef.current = true;
      const next = {
        id: createStrokeId(),
        tool,
        color,
        width: strokeWidthFor(tool, thickness),
        points: isShapeTool(tool) ? [point, point] : [point],
        constrain: Boolean(shiftKey) && isShapeTool(tool),
      };
      liveStrokeRef.current = next;
      setLiveStroke(next);
    },
    [color, isDrawingTool, thickness, tool],
  );

  const movePointer = useCallback(
    (clientX, clientY, shiftKey = false) => {
      const point = pointerToNormalized(clientX, clientY, overlayRef.current);
      if (tool === HIGHLIGHT_TOOLS.LASER && toolbarOpen) {
        setLaser(point);
      }
      if (!drawingRef.current) return;
      const current = liveStrokeRef.current;
      if (!current) return;

      if (isShapeTool(current.tool)) {
        const next = {
          ...current,
          points: [current.points[0], point],
          constrain: Boolean(shiftKey),
        };
        liveStrokeRef.current = next;
        setLiveStroke(next);
        return;
      }

      const last = current.points[current.points.length - 1];
      if (last && Math.abs(last.x - point.x) < 0.001 && Math.abs(last.y - point.y) < 0.001) {
        return;
      }
      const next = { ...current, points: [...current.points, point] };
      liveStrokeRef.current = next;
      setLiveStroke(next);
    },
    [toolbarOpen, tool],
  );

  const endStroke = useCallback(() => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const current = liveStrokeRef.current;
    liveStrokeRef.current = null;
    setLiveStroke(null);
    if (!current?.points?.length || isTinyShape(current)) return;
    setStrokes((prev) => [...prev, current]);
    setRedoStack([]);
  }, []);

  const hideLaser = useCallback(() => {
    if (!drawingRef.current) setLaser(null);
  }, []);

  const undoStroke = useCallback(() => {
    setStrokes((prev) => {
      if (!prev.length) return prev;
      setRedoStack((stack) => [...stack, prev[prev.length - 1]]);
      return prev.slice(0, -1);
    });
  }, []);

  const redoStroke = useCallback(() => {
    setRedoStack((stack) => {
      if (!stack.length) return stack;
      setStrokes((prev) => [...prev, stack[stack.length - 1]]);
      return stack.slice(0, -1);
    });
  }, []);

  const clearStrokes = useCallback(() => {
    drawingRef.current = false;
    liveStrokeRef.current = null;
    setLiveStroke(null);
    setStrokes([]);
    setRedoStack([]);
    setLaser(null);
  }, []);

  useEffect(() => {
    if (!toolbarOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setToolbarOpen(false);
      const withMeta = event.ctrlKey || event.metaKey;
      if (withMeta && event.key.toLowerCase() === "z") {
        event.preventDefault();
        if (event.shiftKey) redoStroke();
        else undoStroke();
        return;
      }
      if (withMeta && event.key.toLowerCase() === "y") {
        event.preventDefault();
        redoStroke();
        return;
      }
      if (event.key !== "Shift") return;
      const current = liveStrokeRef.current;
      if (!current || !isShapeTool(current.tool)) return;
      const next = { ...current, constrain: true };
      liveStrokeRef.current = next;
      setLiveStroke(next);
    };
    const onKeyUp = (event) => {
      if (event.key !== "Shift") return;
      const current = liveStrokeRef.current;
      if (!current || !isShapeTool(current.tool)) return;
      const next = { ...current, constrain: false };
      liveStrokeRef.current = next;
      setLiveStroke(next);
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, [redoStroke, toolbarOpen, undoStroke]);

  const value = useMemo(
    () => ({
      canHighlight,
      toolbarOpen,
      tool,
      color,
      thickness,
      strokes,
      liveStroke,
      laser,
      overlayRef,
      isDrawingTool,
      setTool,
      setColor,
      setThickness,
      toggleToolbar,
      closeToolbar,
      beginStroke,
      movePointer,
      endStroke,
      hideLaser,
      undoStroke,
      redoStroke,
      clearStrokes,
      canUndo: strokes.length > 0,
      canRedo: redoStack.length > 0,
    }),
    [
      beginStroke,
      canHighlight,
      clearStrokes,
      closeToolbar,
      color,
      endStroke,
      hideLaser,
      isDrawingTool,
      laser,
      liveStroke,
      movePointer,
      redoStack.length,
      redoStroke,
      strokes,
      thickness,
      toggleToolbar,
      tool,
      toolbarOpen,
      undoStroke,
    ],
  );

  return (
    <ScreenHighlightContext.Provider value={value}>
      {children}
    </ScreenHighlightContext.Provider>
  );
}
